import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Editor } from 'primereact/editor';
import { InputMask } from 'primereact/inputmask';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Badge } from 'primereact/badge';
import CompanyFilterBar from '../components/CompanyFilterBar';

import CompanyLinkInline from '../components/CompanyLinkInline';
import CompanyModal from '../components/CompanyModal';
import SignatureCanvasEditor from '../components/SignatureCanvasEditor';
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { Accordion, AccordionTab } from 'primereact/accordion';

import { useImport } from '../context/ImportContext';
import { importConfigAPI } from '../services/importConfigAPI';
import { globalConfigAPI } from '../services/globalConfigAPI';

import useFilters from '../hooks/useFilters';

export const importEmailTemplates = `<p>Olá <strong>{NOME_DO_CONTATO}</strong>,</p><p>Solicitamos, gentilmente, o envio dos arquivos necessários para realizarmos a contabilização de sua empresa. Abaixo constam os tipos de arquivo e os meses pendentes para envio. </p><p>Para este envio, clique no botão azul "Enviar" ao final deste e-mail e anexe os documentos conforme o nome e o mês sugeridos na tela seguinte.</p>`;

const Configuration = ({ toastRef, isGlobalConfigSaved, setIsGlobalConfigSaved }) => {

  const emailDocumentRef = useRef('');

  const buildEmailDocument = (sourceDocument, editedHtml) => {
    const source = sourceDocument || editedHtml || '';
    if (/<(?:!doctype|html|head|body)\b/i.test(editedHtml || '')) return editedHtml;
    const bodyStart = source.search(/<body\b[^>]*>/i);
    const bodyEnd = source.search(/<\/body\s*>/i);
    if (bodyStart === -1 || bodyEnd === -1 || bodyEnd < bodyStart) return editedHtml || source;

    const bodyTagEnd = source.indexOf('>', bodyStart) + 1;
    return `${source.slice(0, bodyTagEnd)}${editedHtml || ''}${source.slice(bodyEnd)}`;
  };

  const [accordionActiveIndex, setAccordionActiveIndex] = useState(isGlobalConfigSaved ? null : 0);
  // Força o Accordion a abrir caso 'isGlobalConfigSaved' mude de estado ou chegue como falso
  useEffect(() => {
    if (isGlobalConfigSaved === false) {
      setAccordionActiveIndex(0);
    }
  }, [isGlobalConfigSaved]);

  const { loading: contextLoading, setLoading } = useImport();

  const [fullscreenTable, setFullscreenTable] = useState(false);

  const [globalConfig, setGlobalConfig] = useState({
    start_day: 5,
    start_month: null,
    repetitions_per_month: 3,
    interval_days: 10,
    email_from_name: 'Lance S Importações',
    email_from_email: 'noreply@lancesimples.com',
    email_signature: '',
    body_email: importEmailTemplates,
    whatsapp_enabled: false,
    active: true
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [configurations, setConfigurations] = useState([]);

  const [globalLoading, setGlobalLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  const [newGlobalConfig, setNewGlobalConfig] = useState({ ...globalConfig });
  const [globalConfigLoadedOnce, setGlobalConfigLoadedOnce] = useState(false);

  // Modal: Edição do Corpo do E-mail
  const [emailBodyModalVisible, setEmailBodyModalVisible] = useState(false);
  const [tempBodyEmail, setTempBodyEmail] = useState(importEmailTemplates);
  const [savingBodyEmail, setSavingBodyEmail] = useState(false);

  const [selectedConfig, setSelectedConfig] = useState(null);

  // Modal: Excluir
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Modal: Cadastro/Edição de Cliente
  const [enterpriseModalVisible, setEnterpriseVisible] = useState(false);
  const [isClientFormDisabled, setIsClientFormDisabled] = useState(false);
  const [showCadastroCard, setShowCadastroCard] = useState(true);

  // CONTROLADORES DE FLUXO/REQUISITOS
  const [showAccordion, setShowAccordion] = useState(false);
  const [companyModalVisible, setCompanyModalVisible] = useState(false);

  // Modal UC01: Confirmação de Alteração de Data Inicial
  const [dateChangeConfirmVisible, setDateChangeConfirmVisible] = useState(false);
  const [dateChangeMessage, setDateChangeMessage] = useState('');

  const [totalItensLayout, setTotalItensLayout] = useState(0);
  const [withoutLayoutCount, setWithoutLayoutCount] = useState(0);
  const [isWithoutLayoutFilterActive, setIsWithoutLayoutFilterActive] = useState(false);
  const [totalEnterprisesCount, setTotalEnterprisesCount] = useState(0);

  // Lista de usuários para o dropdown de Responsável
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);


  const [newConfig, setNewConfig] = useState({
    id: 0,
    enterprise_name: '',
    enterprise_email: '',
    employee_name: '',
    responsible_id: null,
    responsible_name: null,
    whatsapp_contact: '',
    start_day: null,
    start_month: null,
    repetitions_per_month: null,
    interval_days: null,
    active: true
  });

  // Sincroniza estado do accordion caso as props mudem
  useEffect(() => {
    if (!isGlobalConfigSaved) {
      setAccordionActiveIndex(0);
    }
  }, [isGlobalConfigSaved]);

  // Localiza o usuário correspondente tanto por ID quanto por Nome (para garantir preenchimento no Edit)
  const selectedUser = users.find(u =>
    (newConfig.responsible_id && Number(u.id) === Number(newConfig.responsible_id)) ||
    (newConfig.responsible_name && u.name === newConfig.responsible_name)
  ) || null;

  const countWithoutLayoutCount = async () => {
    try {
      const response = await importConfigAPI.countWithoutLayout();
      const count = typeof response === 'number' ? response : (response?.total ?? response?.data ?? 0);
      setWithoutLayoutCount(count);
    } catch {
      setWithoutLayoutCount(0);
    }
  };

  const countEnterprises = async () => {
    try {
      const response = await importConfigAPI.countEnterprises();
      const count = typeof response === 'number' ? response : (response?.total ?? response?.data ?? 0);
      setTotalEnterprisesCount(count);
    } catch {
      setTotalEnterprisesCount(0);
    }
  };

  const pollCounters = useCallback(async () => {
    try {
      await countWithoutLayoutCount();
      await countEnterprises();
    } catch {
      // Silencia erros no polling
    }
  }, []);


  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await importConfigAPI.listUsers();
      if (response?.success) {
        setUsers(response.data || []);
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };


  const [activeStep, setActiveStep] = useState(0);

  const { filters, setFilters } = useFilters({
    page: 1,
    limit: 10,
    enterprise_name: null,
    enterprise_email: null,
    responsible_name: null
  });


  const coerceToCalendarDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
      const [y, m] = value.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(num) && num >= 1 && num <= 12) {
      const now = new Date();
      return new Date(now.getFullYear(), num - 1, 1);
    }
    return null;
  };

  const calendarDateToApiValue = (date) => {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}`;
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const openDeleteConfirm = (id) => {
    setDeleteConfirmId(id);
    setDeleteConfirmVisible(true);
  };

  const cancelDelete = () => {
    setDeleteConfirmVisible(false);
  };

  const handleDeleteEnterprise = async (id) => {
    try {
      await importConfigAPI.delete(id);
      toastRef.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Cliente excluído com sucesso!' });
      loadConfigurations(filters);
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao excluir' });
    }
  };


  const confirmDelete = async () => {
    const id = deleteConfirmId;
    setDeleteConfirmVisible(false);
    if (!id) return;
    await handleDeleteEnterprise(id);
  };

  const loadGlobalConfig = async () => {
    setGlobalLoading(true);
    try {
      const response = await globalConfigAPI.get();
      if (response.success) {
        const data = {
          ...response.data,
          body_email: (response.data?.body_email && response.data.body_email.trim()) ? response.data.body_email : importEmailTemplates
        };
        setGlobalConfig(data);
        setNewGlobalConfig(data);
      }
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar config global' });
    } finally {
      setGlobalLoading(false);
      setGlobalConfigLoadedOnce(true);
    }
  };

  const loadConfigurations = async (filterParams = {}) => {
    setCompaniesLoading(true);
    try {
      const payload = {
        enterprise_name: filterParams.enterprise_name,
        enterprise_email: filterParams.enterprise_email,
        responsible_name: filterParams.responsible_name,
        withoutLayout: filterParams.withoutLayout === true ? true : undefined,
        page: filterParams.page,
        limit: filterParams.limit
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });
      const response = await importConfigAPI.list(payload);

      countWithoutLayoutCount();
      countEnterprises();
      setTotalRecords(response.pagination?.total || 0);
      setConfigurations(response.data || []);
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar empresas' });
    } finally {
      setCompaniesLoading(false);
    }
  };

  useEffect(() => {
    loadGlobalConfig();
    if (isGlobalConfigSaved) {
      loadConfigurations(filters);
      countWithoutLayoutCount();
      countEnterprises();
    }
  }, [filters, isGlobalConfigSaved]);

  const pollingRef = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } else {
        pollingRef.current = setInterval(() => {
          pollCounters();
        }, 5000);
      }
    };

    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pollCounters]);


  const saveGlobalConfig = async () => {
    setGlobalLoading(true);
    try {
      await globalConfigAPI.save(newGlobalConfig);
      setGlobalConfig(newGlobalConfig);
      // Atualiza o estado indicando que agora a config global está salva!
      if (setIsGlobalConfigSaved) {
        setIsGlobalConfigSaved(true);
      }
      toastRef.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Configuração global salva com sucesso! Agora você já pode cadastrar novos clientes.' });
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao salvar config global' });
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleOpenEmailBodyModal = async () => {
    try {
      const response = await globalConfigAPI.get();
      const currentBody = (response?.data?.body_email && response.data.body_email.trim())
        ? response.data.body_email
        : (newGlobalConfig.body_email && newGlobalConfig.body_email.trim())
          ? newGlobalConfig.body_email
          : importEmailTemplates;
      emailDocumentRef.current = currentBody;
      setTempBodyEmail(currentBody);
      if (response?.data) {
        setNewGlobalConfig((prev) => ({ ...prev, ...response.data, body_email: currentBody }));
        setGlobalConfig((prev) => ({ ...prev, ...response.data, body_email: currentBody }));
      }
    } catch {
      const currentBody = (newGlobalConfig.body_email && newGlobalConfig.body_email.trim())
        ? newGlobalConfig.body_email
        : importEmailTemplates;
      emailDocumentRef.current = currentBody;
      setTempBodyEmail(currentBody);
    }
    setEmailBodyModalVisible(true);
  };

  const handleRestoreEmailSignature = async () => {
    const currentBody = importEmailTemplates;
    emailDocumentRef.current = currentBody;
    setTempBodyEmail(currentBody);
     const updatedGlobal = {
      ...newGlobalConfig,
      body_email: currentBody
    };
    await globalConfigAPI.save(updatedGlobal);
    setGlobalConfig(updatedGlobal);
    setNewGlobalConfig(updatedGlobal);
    toastRef.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Texto do e-mail restaurado com sucesso!' });
  };

  const handleSaveEmailBody = async () => {
    setSavingBodyEmail(true);
    const bodyEmail = buildEmailDocument(emailDocumentRef.current, tempBodyEmail);
    const updatedGlobal = {
      ...newGlobalConfig,
      body_email: bodyEmail
    };
    try {
      await globalConfigAPI.save(updatedGlobal);
      setGlobalConfig(updatedGlobal);
      setNewGlobalConfig(updatedGlobal);
      if (setIsGlobalConfigSaved) {
        setIsGlobalConfigSaved(true);
      }
      setEmailBodyModalVisible(false);
      toastRef.current?.show({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Texto do e-mail salvo com sucesso!'
      });
    } catch {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: 'Falha ao salvar texto do e-mail'
      });
    } finally {
      setSavingBodyEmail(false);
    }
  };

  const [configIdForLinking, setConfigIdForLinking] = useState(null);

  const handleLinkCompanies = (rowData) => {
    setConfigIdForLinking(rowData.id);
    setCompanyModalVisible(true);
  };

  const formatMonthYear = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && val.includes('-')) {
      const parts = val.split('-');
      if (parts.length === 2) {
        return `${parts[1]}/${parts[0]}`;
      }
    }
    return val;
  };

  // AÇÃO: Clicou em Editar na Tabela
  const handleEditEnterprise = (rowData) => {
    // Garante o carregamento dos usuários e atualiza o estado com o ID e Nome do responsável vindo da linha
    loadUsers();
    setNewConfig({
      ...rowData,
      responsible_id: rowData.responsible_id || null,
      responsible_name: rowData.responsible_name || null
    });
    setConfigIdForLinking(rowData.id);
    setShowAccordion(false);
    setIsClientFormDisabled(false);
    setEnterpriseVisible(true);
  };

  // 3. Validação ao clicar no botão "Novo Cliente"
  const handleOpenNewClientModal = () => {
    if (!isGlobalConfigSaved) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Atenção - Configuração Obrigatória',
        detail: 'Você precisa salvar as configurações globais antes de cadastrar um novo cliente.',
        life: 5000
      });
      // Garante que a sanfona das configurações globais esteja aberta para orientar o usuário
      setAccordionActiveIndex(0);
      return;
    }
    // Fluxo normal de abertura do modal de Novo Cliente
    setNewConfig({
      id: 0,
      enterprise_name: '',
      enterprise_email: '',
      employee_name: '',
      whatsapp_contact: '',
      responsible_id: null,
      responsible_name: null,
      start_day: globalConfig.start_day,
      start_month: globalConfig.start_month ?? null,
      repetitions_per_month: globalConfig.repetitions_per_month,
      interval_days: globalConfig.interval_days,
      active: true
    });
    setShowAccordion(true);
    setActiveStep(0);
    setEnterpriseVisible(true);
    setConfigIdForLinking(null);
    setIsClientFormDisabled(false);
    setShowCadastroCard(true);
    loadUsers();
  };

  const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    const value = email.trim();
    if (!value) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  };

  const handleSaveConfig = async (overrideFlags = null) => {
    const enterpriseEmail = (newConfig.enterprise_email ?? '').toString().trim();

    if (!newConfig.enterprise_name || enterpriseEmail.length === 0 || !newConfig.responsible_name) {
      toastRef.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Nome da empresa, e-mail e responsável são obrigatórios' });
      return;
    }

    if (!isValidEmail(enterpriseEmail)) {
      toastRef.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe um e-mail válido para o cliente' });
      return;
    }

    setLoading(true);

    const payload = overrideFlags
      ? { ...newConfig, ...overrideFlags }
      : { ...newConfig };

    try {
      const saved = await importConfigAPI.save(payload);
      loadConfigurations(filters);

      if (newConfig.id === 0) {
        const savedId = saved?.data?.id;
        if (savedId) {
          setConfigIdForLinking(savedId);
          setIsClientFormDisabled(true);
          setShowCadastroCard(false);
          setActiveStep(1);
        }
      } else {
        setEnterpriseVisible(false);
      }
      toastRef.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Configurações salvas com sucesso!' });
    } catch (error) {
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.error ||
        responseData?.message ||
        responseData?.detail ||
        responseData?.errors?.[0] ||
        null;

      const code = responseData?.code || responseData?.error_code || responseData?.status;
      const requiresConfirmation =
        responseData?.requires_confirmation === true ||
        responseData?.confirm_required === true ||
        responseData?.confirm_date_change === true ||
        code === 'CONFIRM_DATE_CHANGE' ||
        code === 'REQUIRES_CONFIRMATION' ||
        code === 'HISTORIC_IMPACT' ||
        error?.response?.status === 409 ||
        (Boolean(apiMessage) && (
          apiMessage.toLowerCase().includes('confir') ||
          apiMessage.toLowerCase().includes('impactar') ||
          apiMessage.toLowerCase().includes('excluir') ||
          apiMessage.toLowerCase().includes('expurgo') ||
          apiMessage.toLowerCase().includes('movimentaç') ||
          apiMessage.toLowerCase().includes('historico') ||
          apiMessage.toLowerCase().includes('histórico')
        ));

      // Se a API retornou um erro de negócio que requer confirmação e ainda não enviamos o override
      if (requiresConfirmation && !overrideFlags) {
        const formattedDate = formatMonthYear(newConfig.start_month);
        const fallbackMsg = `A alteração da data inicial para ${formattedDate} impactará as movimentações já importadas. Deseja confirmar a alteração?`;

        setDateChangeMessage(apiMessage || fallbackMsg);
        setDateChangeConfirmVisible(true);
        return;
      }

      const fallbackMessage = 'Falha ao salvar';
      const isBusinessWarning = Boolean(apiMessage) && !error?.response?.data?.success;

      toastRef.current?.show({
        severity: isBusinessWarning ? 'warn' : 'error',
        summary: isBusinessWarning ? 'Atenção' : 'Erro',
        detail: apiMessage || fallbackMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDateChange = async () => {
    setDateChangeConfirmVisible(false);
    await handleSaveConfig({
      confirm_date_change: true,
      override: true,
      override_date_change: true,
      force: true
    });
  };

  const handleCancelDateChange = () => {
    setDateChangeConfirmVisible(false);
  };

  const applyWithoutLayoutFilter = (enabled) => {
    setIsWithoutLayoutFilterActive(!!enabled);
    setFilters(prev => {
      const base = { ...prev, page: 1 };
      if (enabled) {
        return { ...base, withoutLayout: true };
      }
      const { withoutLayout, ...rest } = base;
      return rest;
    });
  };

  const clearWithoutLayoutFilter = () => {
    applyWithoutLayoutFilter(false);
  };

  const globalConfigHeader = (
    <div className="flex justify-between items-center">
      <span className="text-lg font-bold text-gray-700">⚙️ Configuração Global</span>
    </div>
  );

  const companiesHeader = (
    <div className="flex justify-between items-center w-full">
      <div className="flex items-center">
        <span className="text-lg font-bold text-gray-700">🏢 Configurações por Cliente</span>
      </div>

      <Button
        type="button"
        icon={fullscreenTable ? 'pi pi-window-minimize' : 'pi pi-window-maximize'}
        className="p-button-text p-button-sm"
        onClick={() => setFullscreenTable((prev) => !prev)}
        aria-label={fullscreenTable ? 'Sair do modo amplo' : 'Entrar no modo amplo'}
      />
    </div>
  );

  return (
    <div
      className={`flex flex-column gap-3 p-2 bg-gray-100 h-screen w-full overflow-hidden ${fullscreenTable ? 'p-0 bg-transparent' : ''}`}
    >
      <ConfirmDialog />

      <CompanyModal
        visible={companyModalVisible}
        onHide={() => {
          setCompanyModalVisible(false);
          setConfigIdForLinking(null);
        }}
        configId={configIdForLinking}
      />

      <Dialog
        header={newConfig.id === 0 ? "Cadastro de Cliente" : "Editar Cliente"}
        visible={enterpriseModalVisible}
        style={{ width: '70vw' }}
        breakpoints={{ '960px': '85vw', '641px': '100vw' }}
        className="p-fluid"
        onHide={() => {
          setEnterpriseVisible(false);
          setConfigIdForLinking(null);
          setIsClientFormDisabled(false);
          setShowAccordion(false);
          setShowCadastroCard(true);
        }}
      >
        <Stepper activeStep={activeStep} onStepChange={(e) => setActiveStep(e.index)}>
          <StepperPanel header="Informações do Cliente"
            pt={{
              header: {
                style: newConfig.id !== 0 ? { display: 'none' } : {}
              }
            }}
          >
            <div className="flex flex-col h-auto pt-2">
              <div className={`bg-white p-5 rounded-xl border border-gray-100 shadow-2 border-round-xl space-y-4 transition-all ${isClientFormDisabled ? 'opacity-60 pointer-events-none select-none' : ''}`}>
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3 flex items-center gap-2">
                  <i className="pi pi-user text-blue-500 text-base"></i>
                  Informações do Cliente {isClientFormDisabled && <span className="text-xs text-green-600 font-normal normal-case">(Salvo com sucesso)</span>}
                </h3>

                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 md:col-span-6 flex flex-col gap-1.5">
                    <label className="font-semibold text-xs text-gray-600">Cliente *</label>
                    <InputText
                      placeholder="Insira o nome do cliente..."
                      value={newConfig.enterprise_name}
                      onChange={(e) => setNewConfig({ ...newConfig, enterprise_name: e.target.value })}
                      className="w-full"
                      disabled={isClientFormDisabled}
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6 flex flex-col gap-1.5">
                    <label className="font-semibold text-xs text-gray-600">Email do Cliente *</label>
                    <InputText
                      placeholder="Insira o email do cliente..."
                      value={newConfig.enterprise_email}
                      onChange={(e) => setNewConfig({ ...newConfig, enterprise_email: e.target.value })}
                      className="w-full"
                      disabled={isClientFormDisabled}
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6 flex flex-col gap-1.5">
                    <label className="font-semibold text-xs text-gray-600">Nome do Contato</label>
                    <InputText
                      placeholder="Insira o nome do contato..."
                      value={newConfig.employee_name}
                      onChange={(e) => setNewConfig({ ...newConfig, employee_name: e.target.value })}
                      className="w-full"
                      disabled={isClientFormDisabled}
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6 flex flex-col gap-1.5">
                    <label className="font-semibold text-xs text-gray-600">Contato WhatsApp</label>
                    <InputMask
                      mask="(99) 99999-9999"
                      placeholder="Insira o contato..."
                      value={newConfig.whatsapp_contact}
                      onChange={(e) => setNewConfig({ ...newConfig, whatsapp_contact: e.value })}
                      className="w-full"
                      disabled={isClientFormDisabled}
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6 flex flex-col gap-1.5">
                    <label className="font-semibold text-xs text-gray-600">Nome do Responsável *</label>
                    <Dropdown
                      value={selectedUser}
                      options={users}
                      onChange={(e) => {
                        const user = e.value;
                        setNewConfig({
                          ...newConfig,
                          responsible_id: user?.id || null,
                          responsible_name: user?.name || null,
                        });
                      }}
                      optionLabel="name"
                      showClear
                      valueTemplate={(opt) => {
                        if (!opt) return <span className="text-gray-400">Selecione um contato...</span>;
                        return (
                          <div className="flex align-items-center gap-2">
                            <i className="pi pi-user text-sm text-gray-500"></i>
                            <strong className="font-bold">{opt.name}</strong>
                          </div>
                        );
                      }}
                      itemTemplate={(opt) => {
                        return (
                          <div className="flex align-items-center gap-2">
                            <i className="pi pi-user text-sm text-gray-500"></i>
                            <span>
                              <strong className="font-bold">{opt.name || ''}</strong> {opt.email ? `<${opt.email}>` : ''}
                            </span>
                          </div>
                        );
                      }}
                      placeholder="Selecione um contato..."
                      loading={usersLoading}
                      filter
                      filterBy="name,nome,email"
                      className="w-full"
                      disabled={isClientFormDisabled}
                    />
                  </div>

                  <div style={{ marginLeft: '5px', marginRight: '5px' }} className="col-span-12 grid grid-cols-1 sm:grid-cols-4 gap-3 pt-2">
                    <div className="flex flex-col gap-1.5">
                      <label className="font-semibold text-xs text-gray-600">Dia Início</label>
                      <InputNumber
                        value={newConfig.start_day}
                        onValueChange={(e) => setNewConfig({ ...newConfig, start_day: e.value })}
                        max={31}
                        className="w-full"
                        disabled={isClientFormDisabled}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="font-semibold text-xs text-gray-600">Intervalo/Dias</label>
                      <InputNumber
                        value={newConfig.interval_days}
                        onValueChange={(e) => setNewConfig({ ...newConfig, interval_days: e.value })}
                        className="w-full"
                        disabled={isClientFormDisabled}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="font-semibold text-xs text-gray-600">Repetições/Mês</label>
                      <InputNumber
                        value={newConfig.repetitions_per_month}
                        onValueChange={(e) => setNewConfig({ ...newConfig, repetitions_per_month: e.value })}
                        className="w-full"
                        disabled={isClientFormDisabled}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="font-semibold text-xs text-gray-600">Mês/Ano Inicial</label>
                      <Calendar
                        value={coerceToCalendarDate(newConfig.start_month)}
                        showButtonBar
                        onChange={(e) => setNewConfig({ ...newConfig, start_month: calendarDateToApiValue(e.value) })}
                        view="month"
                        dateFormat="mm/yy"
                        placeholder="Início"
                        locale="pt-BR"
                        readOnlyInput
                        className="w-full"
                        disabled={isClientFormDisabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex pt-4 justify-content-end">
              <Button
                label={newConfig.id === 0 ? "Próximo" : "Salvar"}
                icon={newConfig.id === 0 ? "pi pi-arrow-right" : "pi pi-save"}
                iconPos="right"
                onClick={() => { handleSaveConfig() }}
                loading={contextLoading}
                className="w-auto"
              />
            </div>
          </StepperPanel>
          {newConfig.id === 0 && (
            <StepperPanel header="Vínculo de Empresa">
              <div className="flex flex-col h-auto pt-2">
                <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-2 border-round-xl space-y-4">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3 flex items-center gap-2">
                    <i className="pi pi-share-alt text-base text-green-500"></i>
                    Vincular Empresa - Layout
                  </h3>
                  <div className="pt-2">
                    <div className="flex flex-col gap-1">
                      <CompanyLinkInline itensLayout={setTotalItensLayout} configId={configIdForLinking} />
                      {totalItensLayout === 0 && (
                        <span className="text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded normal-case flex items-center gap-1">
                          <i className="pi pi-lock text-[10px]"></i> Vincule pelo menos um layout para finalizar o cadastro do cliente
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex pt-4 justify-content-end gap-2">
                <Button
                  label="Finalizar"
                  icon="pi pi-check"
                  disabled={totalItensLayout <= 0}
                  severity="success"
                  onClick={() => {
                    toastRef.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Configuração salva' });
                    setEnterpriseVisible(false);
                    setConfigIdForLinking(null);
                    setIsClientFormDisabled(false);
                    setShowAccordion(false);
                    setShowCadastroCard(true);
                    setActiveStep(0);
                  }}
                  className="w-auto"
                />
              </div>
            </StepperPanel>
          )}
        </Stepper>
      </Dialog>

      {/* Modal Excluir */}
      <Dialog
        visible={deleteConfirmVisible}
        onHide={cancelDelete}
        modal
        closable={false}
        style={{ width: '100%', maxWidth: '550px' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      >
        <div className="flex flex-column bg-white">
          <div className="flex align-items-start gap-2 mb-4">
            <div
              className="flex align-items-center justify-content-center bg-yellow-50 text-yellow-600 border-circle surface-overlay shadow-1"
              style={{ width: '48px', height: '48px', flexShrink: 0 }}
            >
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem' }} />
            </div>
            <div className="flex flex-column gap-1 pt-1">
              <h3 className="m-0 text-xl font-bold text-gray-900">Confirmar Exclusão</h3>
              <p className="m-0 text-sm text-gray-500 line-height-3">Deseja realmente excluir esse cliente?</p>
            </div>
          </div>

          <div className="flex justify-content-end gap-2 border-top-1 border-100 pt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={cancelDelete}
              className="p-button-outlined p-button-secondary p-button-sm border-round-md font-semibold px-3"
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              onClick={confirmDelete}
              className="p-button-danger p-button-sm border-round-md font-semibold px-3"
            />
          </div>
        </div>
      </Dialog>

      {/* Modal UC01: Confirmação de Alteração de Data Inicial */}
      <Dialog
        visible={dateChangeConfirmVisible}
        onHide={handleCancelDateChange}
        modal
        closable={false}
        style={{ width: '100%', maxWidth: '550px' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      >
        <div className="flex flex-column bg-white">
          <div className="flex align-items-start gap-2 mb-4">
            <div
              className="flex align-items-center justify-content-center bg-yellow-50 text-yellow-600 border-circle surface-overlay shadow-1"
              style={{ width: '48px', height: '48px', flexShrink: 0 }}
            >
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem' }} />
            </div>
            <div className="flex flex-column gap-1 pt-1">
              <h3 className="m-0 text-xl font-bold text-gray-900">Confirmar Alteração de Data Inicial</h3>
              <p className="m-0 text-sm text-gray-600 line-height-3">{dateChangeMessage}</p>
            </div>
          </div>

          <div className="flex justify-content-end gap-2 border-top-1 border-100 pt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={handleCancelDateChange}
              className="p-button-outlined p-button-secondary p-button-sm border-round-md font-semibold px-3"
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              onClick={handleConfirmDateChange}
              className="p-button-warning p-button-sm border-round-md font-semibold px-3"
              loading={contextLoading}
            />
          </div>
        </div>
      </Dialog>

      {/* Modal: Edição do Corpo do E-mail */}
      <Dialog
        header="Editar Texto do E-mail (Template)"
        visible={emailBodyModalVisible}
        style={{ width: '800px', maxWidth: '95vw' }}
        modal
        onHide={() => setEmailBodyModalVisible(false)}
        footer={
          <div className="flex justify-end justify-content-end items-center gap-2 w-full">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text p-button-sm"
              onClick={() => setEmailBodyModalVisible(false)}
              type="button"
            />
            <Button
              label="Salvar"
              icon="pi pi-save"
              loading={savingBodyEmail}
              onClick={handleSaveEmailBody}
              type="button"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3 pt-2">
          <p className="text-xs text-gray-600 m-0">
            Personalize o modelo de texto do e-mail que será enviado para os clientes solicitando a importação de arquivos:
          </p>
          <Editor
            value={tempBodyEmail}
            onTextChange={(e) => setTempBodyEmail(e.htmlValue || '')}
            style={{ height: '320px' }}
          />
        </div>
      </Dialog>

      {!fullscreenTable && (
        <Accordion style={{ marginLeft: '2px' }} activeIndex={accordionActiveIndex} className="shadow-2 border-round-xl overflow-hidden bg-white mb-2">
          <AccordionTab
            header={
              <div className="flex items-center gap-2">
                <i className="pi pi-cog text-blue-500"></i>
                <span className="font-bold text-gray-700 text-base">Configuração Global</span>
                {!isGlobalConfigSaved && (
                  <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded font-semibold ml-2">
                    Preenchimento Obrigatório
                  </span>
                )}
              </div>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 pt-2">
              <div className="flex flex-col gap-1.5">
                <label className="font-semibold text-xs text-gray-600">Dia Início</label>
                <InputNumber
                  value={newGlobalConfig.start_day}
                  onValueChange={(e) => setNewGlobalConfig({ ...newGlobalConfig, start_day: e.value })}
                  min={1}
                  max={28}
                  className="w-full p-inputtext-sm"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-semibold text-xs text-gray-600">Intervalo/Dias</label>
                <InputNumber
                  value={newGlobalConfig.interval_days}
                  onValueChange={(e) => setNewGlobalConfig({ ...newGlobalConfig, interval_days: e.value })}
                  min={1}
                  max={60}
                  className="w-full p-inputtext-sm"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-semibold text-xs text-gray-600">Repetições/Mês</label>
                <InputNumber
                  value={newGlobalConfig.repetitions_per_month}
                  onValueChange={(e) => setNewGlobalConfig({ ...newGlobalConfig, repetitions_per_month: e.value })}
                  min={1}
                  max={31}
                  className="w-full p-inputtext-sm"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-semibold text-xs text-gray-600">Mês/Ano Inicial</label>
                <Calendar
                  id="competenceStartMonthFilter"
                  value={coerceToCalendarDate(newGlobalConfig.start_month)}
                  showButtonBar
                  onChange={(e) => setNewGlobalConfig({ ...newGlobalConfig, start_month: calendarDateToApiValue(e.value) })}
                  view="month"
                  dateFormat="mm/yy"
                  locale="pt-BR"
                  placeholder="Início"
                  readOnlyInput
                  className="w-full p-inputtext-sm"
                />
              </div>

              <div className="flex flex-col gap-1.5 justify-end">
                <Button
                  label="Salvar Global"
                  icon="pi pi-save"
                  loading={globalLoading}
                  onClick={saveGlobalConfig}
                  className="h-[38px] w-full"
                />
              </div>

              <div className="col-span-12 flex flex-col gap-1.5 mt-2">
                <label className="font-semibold text-xs text-gray-600">Assinatura do e-mail</label>
                <SignatureCanvasEditor
                  key={globalConfigLoadedOnce ? 'signature-loaded' : 'signature-loading'}
                  value={newGlobalConfig.email_signature}
                  onChange={(html) => setNewGlobalConfig((prev) => ({ ...prev, email_signature: html }))}
                  toastRef={toastRef}
                  onEditText={handleOpenEmailBodyModal}
                  onRestoreText={handleRestoreEmailSignature}
                />
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      )}

      <div className="bg-white border-round-lg shadow-1 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-grow-1 w-full">
          <CompanyFilterBar filters={filters} onFilterChange={handleFilterChange} />
        </div>
        <Button
          style={{ marginRight: '10px' }}
          label="Novo Cliente"
          icon="pi pi-plus"
          severity="success"
          onClick={() => {
            handleOpenNewClientModal()
          }}
          className="font-semibold h-[40px] min-w-[200px] whitespace-nowrap self-end sm:self-auto margin-right"
        />
      </div>
      <Card
        header={companiesHeader}
        className={`shadow-2 border-round-xl overflow-hidden flex flex-column flex-1 ${fullscreenTable ? 'w-full h-full' : ''}`}
        pt={{
          header: { className: 'p-0 pt-3 px-3 flex-shrink-0' },
          body: { className: 'p-3 pt-1 flex flex-column flex-1 min-h-0 overflow-hidden' },
          content: { className: 'p-0 flex flex-column flex-1 min-h-0 overflow-hidden' }
        }}
        style={{ marginBottom: '35px' }}
      >
        <>
          <div style={{ marginLeft: '10px' }} className="flex justify-between items-center mb-2 mt-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-normal text-gray-600 normal-case flex items-center gap-1">
                Total de Clientes Cadastrados:
              </span>
              <Badge value={totalEnterprisesCount} severity="info" size="normal" style={{ borderRadius: '9999px' }} />
            </div>

            {typeof withoutLayoutCount === 'number' && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-normal text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded normal-case flex items-center gap-1">
                  Clientes sem Empresa(s) vinculada(s):
                </span>
                <Badge
                  value={withoutLayoutCount}
                  severity="warning"
                  size="normal"
                  className="cursor-pointer"
                  onClick={() => applyWithoutLayoutFilter(true)}
                  style={{
                    background: isWithoutLayoutFilterActive ? '#f59e0b' : undefined,
                    borderRadius: '9999px',
                  }}
                />
                {isWithoutLayoutFilterActive && (
                  <Button
                    label="Limpar"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={clearWithoutLayoutFilter}
                  />
                )}
              </div>
            )}
          </div>

          <div style={{ marginLeft: '5px' }} className="flex flex-column flex-1 h-full min-h-0 overflow-hidden">
            <DataTable
              value={configurations}
              lazy
              paginator
              selectionMode="single"
              emptyMessage="Nenhum dado para visualizar. Clique 'Novo Cliente' para adicionar."
              selection={selectedConfig}
              onSelectionChange={(e) => setSelectedConfig(e.value)}
              rows={filters.limit}
              first={(filters.page - 1) * filters.limit}
              totalRecords={totalRecords}
              onPage={(e) => setFilters((prev) => ({ ...prev, page: e.page + 1, limit: e.rows }))}
              loading={companiesLoading}
              responsiveLayout="scroll"
              scrollable
              scrollHeight="flex"
              className="p-datatable-sm flex-1 flex flex-column justify-content-between h-full overflow-hidden"
              pt={{
                wrapper: { className: 'flex-1 overflow-auto' },
                paginator: { className: 'mt-auto border-top-1 surface-border flex-shrink-0' }
              }}
            >
              <Column field="enterprise_name" className='text-sm font-medium text-gray-700' header="Cliente" sortable style={{ width: '20%' }} />
              <Column
                field="enterprise_email"
                header="Contato"
                style={{ width: '25%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              />
              <Column field="responsible_name" className='text-sm font-medium text-gray-700' header="Responsável" style={{ width: '18%' }} />
              <Column field="start_day" className='text-sm font-medium text-gray-700' header="Dia Início" style={{ width: '9%' }} />
              <Column field="interval_days" header="Intervalo/Dias" style={{ width: '10%' }} />
              <Column field="repetitions_per_month" header="Repetições/Mês" style={{ width: '10%' }} />
              <Column
                field="start_month"
                header="Mês/Ano Inicial"
                className='text-sm font-medium text-gray-700'
                style={{ width: '10%' }}
                body={(rowData) => {
                  if (!rowData.start_month) return '';
                  const parts = rowData.start_month.split('-');
                  return parts.length === 2 ? `${parts[1]}-${parts[0]}` : rowData.start_month;
                }}
              />


              <Column
                header="Ações"
                frozen
                alignFrozen="right"
                style={{ width: '140px' }}
                body={(rowData) => (
                  <div className="flex gap-1 justify-center">
                    <Button icon="pi pi-link" style={{ height: '15px' }} tooltip="Vincular Empresas" tooltipOptions={{ position: 'left' }} severity="info" size="small" text onClick={() => handleLinkCompanies(rowData)} />
                    <Button icon="pi pi-pencil" style={{ height: '15px' }} tooltip="Editar" tooltipOptions={{ position: 'left' }} severity="secondary" size="small" text onClick={() => handleEditEnterprise(rowData)} />
                    <Button icon="pi pi-trash" style={{ height: '15px' }} tooltip="Excluir" tooltipOptions={{ position: 'left' }} severity="danger" size="small" text onClick={() => openDeleteConfirm(rowData.id)} />
                  </div>
                )}
              />
            </DataTable>
          </div>
        </>
      </Card>

    </div>
  );
};

export default Configuration;